import styled from '@emotion/styled';
import { CircularProgress, Table } from '@mui/material';
import tw from 'twin.macro';

import RefreshIcon from '../../../assets/svg/RefreshIcon';
import { Button } from '../../Shared';
import { TableBody } from './TableBody';
import { TableHead } from './TableHead';

const StyledCircularProgress = styled(CircularProgress)`
  ${tw`text-brand-300`}
`;

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_DATA_LOADING: 'isDataLoading',
  IS_DATA_ERROR: 'isDataError',
};

const getState = (data, isDataLoading, isDataError) => {
  if (isDataLoading) return STATE.IS_DATA_LOADING;
  if (isDataError) return STATE.IS_DATA_ERROR;
  const hasNoData = data.length === 0;
  if (hasNoData) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

export const ReportTable = ({
  isManager,
  reports,
  selectedApptIDs,
  isReportsLoading,
  isReportsError,
  fetchReports,
  onChangeSelectedApptIDs,
  openActivityLogModal,
  openRescheduleDemoModal,
  onChangeReports,
  filterByStatuses,
}) => {
  const onSelectAllRows = event => {
    const checked = event.target.checked;

    if (checked) {
      const nextSelectedApptIDs = reports.map(({ ApptID }) => ApptID);
      onChangeSelectedApptIDs(nextSelectedApptIDs);
      return;
    }

    onChangeSelectedApptIDs([]);
  };

  const onSelectRow = selectedApptID => {
    onChangeSelectedApptIDs(prev => {
      const isSelected = prev.includes(selectedApptID);

      if (isSelected) {
        const nextSelectedApptIDs = prev.filter(
          ApptID => ApptID !== selectedApptID,
        );

        return nextSelectedApptIDs;
      }

      return [...prev, selectedApptID];
    });
  };

  const isSomeRowsSelected =
    selectedApptIDs.length > 0 && selectedApptIDs.length < reports.length;
  const isAllRowsSelected =
    reports.length > 0 && selectedApptIDs.length === reports.length;
  const state = getState(reports, isReportsLoading, isReportsError);

  return (
    <>
      {(state === STATE.HAS_DATA || state === STATE.HAS_NO_DATA) && (
        <div
          className={
            state === STATE.HAS_DATA ? 'flex-1 overflow-auto' : undefined
          }
        >
          <Table stickyHeader>
            <TableHead
              isManager={isManager}
              isSomeRowsSelected={isSomeRowsSelected}
              isAllRowsSelected={isAllRowsSelected}
              onSelectAllRows={onSelectAllRows}
            />
            {state === STATE.HAS_DATA && (
              <TableBody
                isManager={isManager}
                reports={reports}
                selectedApptIDs={selectedApptIDs}
                onSelectRow={onSelectRow}
                openActivityLogModal={openActivityLogModal}
                openRescheduleDemoModal={openRescheduleDemoModal}
                onChangeReports={onChangeReports}
                filterByStatuses={filterByStatuses}
              />
            )}
          </Table>
        </div>
      )}
      {state === STATE.IS_DATA_LOADING && (
        <div className="h-full flex justify-center items-center">
          <StyledCircularProgress size={48} />
        </div>
      )}
      {state === STATE.IS_DATA_ERROR && (
        <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
          <div className="flex justify-center items-center flex-col gap-1.5">
            <span className="text-base-1000 text-[22px]/[28px] font-semibold">
              Failed to load records
            </span>
            <span className="text-base-800 text-[16px]/[24px]">
              Error occurred while loading. Try again.
            </span>
          </div>
          <Button
            title="Reload"
            variant="text"
            className="text-destructive-500 !bg-transparent"
            iconStart={<RefreshIcon className="w-5 h-5 text-destructive-500" />}
            onClick={fetchReports}
          />
        </div>
      )}
      {state === STATE.HAS_NO_DATA && (
        <div className="flex justify-center pt-2 px-6 pb-6">
          <span className="text-base-800 text-[16px]/[24px]">
            No available records
          </span>
        </div>
      )}
    </>
  );
};
